import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Receipt, HeartHandshake, Check, CalendarClock, Video, Phone, X, Star, MessageSquare, Loader2 } from "lucide-react";
import { VmaCard } from "@/components/VmaCard";
import { DisplayLevelCard } from "@/components/DisplayLevelCard";
import { TimeVolumeCard } from "@/components/TimeVolumeCard";
import GamificationSection from "@/components/GamificationSection";
import { toast } from "sonner";

function Row({ label, value }) {
  if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-border last:border-0">
      <span className="text-sm text-obsidian/50">{label}</span>
      <span className="text-sm font-medium text-right">{Array.isArray(value) ? value.join(", ") : String(value)}</span>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-3">{title}</div>
      {children}
    </div>
  );
}

export default function Profile() {
  const [d, setD] = useState(null);
  const [billing, setBilling] = useState(null);
  const [rdv, setRdv] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [review, setReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [hoverStar, setHoverStar] = useState(0);
  const [comment, setComment] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const navigate = useNavigate();
  const loadRdv = () => api.get("/booking/current").then((r) => setRdv(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/profile").then((r) => setD(r.data));
    api.get("/payments/history").then((r) => setBilling(r.data)).catch(() => {});
    api.get("/review/me").then((r) => {
      if (r.data.review) { setReview(r.data.review); setRating(r.data.review.rating); setComment(r.data.review.comment || ""); }
    }).catch(() => {});
    loadRdv();
  }, []);

  const saveReview = async () => {
    if (!rating) { toast.error("Choisis une note de 1 à 5 étoiles"); return; }
    setSavingReview(true);
    try {
      const r = await api.post("/review", { rating, comment: comment.trim() });
      setReview(r.data.review);
      toast.success("Merci pour ton avis !");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setSavingReview(false); }
  };

  const cancelRdv = async () => {
    if (!window.confirm("Annuler ce rendez-vous ? Le créneau sera libéré et un crédit te sera recrédité.")) return;
    setCancelling(true);
    try {
      await api.post("/booking/cancel");
      toast.success("Rendez-vous annulé — un crédit t'a été recrédité");
      loadRdv();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setCancelling(false); }
  };

  if (!d) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;
  const p = d.profile?.profile || {}, o = d.profile?.objectives || {}, tr = d.profile?.training || {}, c = d.profile?.constraints || {};
  const booking = rdv?.booking?.booking_id ? rdv.booking : null;

  return (
    <ClientLayout>
      <div className="fade-up space-y-6">
        <div>
          <h1 className="text-3xl font-light tracking-tight">Mon profil</h1>
          <p className="mt-2 text-obsidian/50 text-sm">Structuré automatiquement à partir de ton bilan coureur.</p>
        </div>

        <VmaCard />
        <div className="mt-6"><Card title="Mes disponibilités"><TimeVolumeCard /></Card></div>
        <div className="mt-6"><DisplayLevelCard /></div>

        {(booking || (rdv && rdv.rdv_credits > 0)) && (
          <Card title="Mon rendez-vous">
            {booking ? (
              <div data-testid="profile-rdv" className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarClock size={16} className="text-sage" />
                  <span>Rendez-vous confirmé — <span className="font-medium">{booking.slot_label}</span></span>
                </div>
                {booking.offer_label && <div className="text-sm text-obsidian/60">Prestation : {booking.offer_label}</div>}
                {booking.mode === "phone" ? (
                  <p className="text-sm text-obsidian/70 flex items-center gap-2"><Phone size={14} /> Ton Coach t'appellera au créneau prévu.</p>
                ) : booking.meet_link ? (
                  <a data-testid="profile-rdv-meet" href={booking.meet_link} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-obsidian text-white px-4 py-2 text-sm font-medium hover:-translate-y-0.5 transition-transform"><Video size={14} /> Rejoindre la visio</a>
                ) : (
                  <p className="text-sm text-obsidian/60 flex items-center gap-2"><Video size={14} /> Le lien visio te sera envoyé par email.</p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button data-testid="profile-rdv-reschedule" onClick={() => navigate("/reservation")} disabled={rdv?.can_modify === false}
                    className="rounded-full border border-obsidian px-4 py-2 text-sm font-medium hover:bg-obsidian hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-obsidian disabled:cursor-not-allowed">Reprogrammer</button>
                  <button data-testid="profile-rdv-cancel" onClick={cancelRdv} disabled={cancelling || rdv?.can_modify === false}
                    className="rounded-full border border-red-200 text-red-600 px-4 py-2 text-sm font-medium hover:bg-red-50 transition-colors inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"><X size={14} /> Annuler</button>
                </div>
                {rdv?.can_modify === false && <p className="text-xs text-obsidian/45">Modification impossible à moins de {rdv?.cancellation_window_hours || 24}h du rendez-vous — contacte Ton Coach directement.</p>}
              </div>
            ) : (
              <div className="space-y-3" data-testid="profile-rdv-credits">
                <p className="text-sm text-obsidian/70">Tu disposes de {rdv.rdv_credits} rendez-vous à réserver avec Ton Coach.</p>
                <button data-testid="profile-rdv-book" onClick={() => navigate("/reservation")}
                  className="rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform"><CalendarClock size={15} /> Choisir un créneau</button>
              </div>
            )}
          </Card>
        )}

        <GamificationSection />

        <Card title="Ton avis">
          <div data-testid="review-card" className="space-y-3">
            <p className="text-sm text-obsidian/60">Ton retour nous aide à améliorer l'accompagnement. {review ? "Tu peux le modifier à tout moment." : ""}</p>
            <div className="flex items-center gap-1" data-testid="review-stars" onMouseLeave={() => setHoverStar(0)}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)} onMouseEnter={() => setHoverStar(n)}
                  className="p-0.5 transition-transform hover:scale-110">
                  <Star size={26} strokeWidth={1.6}
                    className={(hoverStar || rating) >= n ? "fill-amber-400 text-amber-400" : "text-obsidian/25"} />
                </button>
              ))}
              {rating > 0 && <span className="ml-2 text-sm text-obsidian/50">{rating}/5</span>}
            </div>
            <textarea data-testid="review-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder="Qu'est-ce qui te plaît ? Qu'est-ce qu'on pourrait améliorer ? (optionnel)"
              className="w-full rounded-xl border border-border px-4 py-3 text-sm focus:ring-1 focus:ring-obsidian focus:outline-none" />
            <button data-testid="review-submit" onClick={saveReview} disabled={savingReview}
              className="rounded-full bg-obsidian text-white px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50">
              {savingReview ? <Loader2 size={15} className="animate-spin" /> : <MessageSquare size={15} />}
              {review ? "Mettre à jour mon avis" : "Envoyer mon avis"}
            </button>
          </div>
        </Card>

        {billing && (
          <Card title="Mon offre & mes reçus">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-sage" />
                <span className="text-sm">Offre actuelle : <span className="font-medium capitalize">{billing.plan || "aucune"}</span>{billing.active ? " · active" : ""}</span>
              </div>
            </div>
            {billing.history?.length > 0 ? (
              <div className="mt-3 space-y-1.5">
                {billing.history.map((h, i) => (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-border last:border-0">
                    <span className="text-obsidian/60">{new Date(h.created_at).toLocaleDateString("fr-FR")} · {h.plan}{h.promo_code ? ` · ${h.promo_code}` : ""}{h.renewal ? " · renouvellement" : ""}</span>
                    <span className="font-medium tabnum">{h.amount} {String(h.currency || "eur").toUpperCase()}</span>
                  </div>
                ))}
              </div>
            ) : <p className="mt-2 text-sm text-obsidian/40">Aucun paiement pour le moment.</p>}

            {billing.plan === "essentiel" && (
              <div className="mt-4 rounded-lg bg-obsidian text-white p-5" data-testid="upgrade-comparatif">
                <div className="flex items-center gap-2"><HeartHandshake size={16} className="text-sage" /><span className="text-sm font-medium">Passe en Premium</span></div>
                <ul className="mt-3 space-y-1.5 text-sm text-white/80">
                  <li className="flex items-center gap-2"><Check size={14} className="text-sage" /> Suivi humain avec Ton Coach</li>
                  <li className="flex items-center gap-2"><Check size={14} className="text-sage" /> Rendez-vous & orientations personnalisées</li>
                </ul>
                <button data-testid="profile-upgrade" onClick={() => navigate("/tarifs")} className="mt-4 rounded-full bg-white text-obsidian px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 transition-transform">Découvrir Premium</button>
              </div>
            )}
          </Card>
        )}
        <Card title="Profil">
          <Row label="Âge" value={p.age} /><Row label="Sexe" value={p.sexe} />
          <Row label="Taille" value={p.taille_cm && `${p.taille_cm} cm`} /><Row label="Poids" value={p.poids_kg && `${p.poids_kg} kg`} />
          <Row label="Activité pro" value={p.activite_pro} /><Row label="Sédentarité" value={p.sedentarite} /><Row label="Expérience" value={p.experience} />
        </Card>
        <Card title="Objectifs">
          <Row label="Principal" value={o.principal} /><Row label="Secondaires" value={o.secondaires} />
          <Row label="Échéance" value={o.echeance} /><Row label="Motivation" value={o.motivation} /><Row label="Tentatives passées" value={o.tentatives_passees} />
        </Card>
        <Card title="Entraînement">
          <Row label="Sports" value={tr.sports} /><Row label="Niveau" value={tr.niveau} />
          <Row label="Fréquence souhaitée" value={tr.frequence_souhaitee} />
          <Row label="Durée / séance" value={tr.duree_seance_min && `${tr.duree_seance_min} min`} /><Row label="Lieu" value={tr.lieu} />
          <Row label="Matériel" value={tr.materiel} /><Row label="Exercices aimés" value={tr.exercices_aimes} /><Row label="Exercices non aimés" value={tr.exercices_non_aimes} />
        </Card>
        <Card title="Contraintes déclarées">
          <Row label="Limitations" value={c.limitations} /><Row label="Douleurs / inconforts" value={c.douleurs} />
          <Row label="Sommeil" value={c.sommeil} /><Row label="Récupération" value={c.recuperation} />
        </Card>
        {d.bilan?.synthesis && (
          <Card title="Analyse IA">
            <ProvenanceBadge type="analyse" className="mb-3" />
            <p className="text-sm text-obsidian/80 leading-relaxed">{d.bilan.synthesis.resume}</p>
          </Card>
        )}
        <div data-testid="profile-support" className="mt-2 rounded-xl border border-border bg-white p-5 text-sm text-obsidian/70">
          Besoin d'aide ou une question ? Contacte notre support :{" "}
          <a data-testid="profile-support-email" href="mailto:supportmairuncoach@gmail.com" className="text-sage font-medium hover:underline">supportmairuncoach@gmail.com</a>
        </div>
      </div>
    </ClientLayout>
  );
}
